/**
 * "Notifiche email" del Support Hub (Task 25): pagina condivisa fra il Centro
 * Ticket admin (`/admin/centro-ticket/notifiche`) e l'area rappresentante
 * (`/area-associativa/supporto/notifiche`).
 *
 * Ogni operatore sceglie come ricevere le email dei ticket: subito, solo in
 * fascia oraria, riepilogo giornaliero o settimanale, nessuna. Le notifiche
 * in-app e la coda restano immediate; le email agli associati non cambiano.
 *
 * `variant` cambia solo il vestito delle card (come GuidePage): `a2-card` per
 * l'admin V2, classi Tailwind della shell per l'area associativa.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Bell, Link2Off, Loader2, Send } from 'lucide-react';
import { useToast } from '../../contexts/ToastContext';
import ConfirmDialog from '../ConfirmDialog';
import type { NotificationPrefsApi, SupportEmailMode, SupportEmailPrefs, SupportEmailPrefsInput } from '../../services/supportApi';

export type NotificationPrefsVariant = 'admin' | 'associative';

/**
 * Solo i campi della modalita scelta: il server valida anche i campi presenti
 * ma inutilizzati, e una fascia oraria lasciata a meta in "hours" non deve far
 * fallire il salvataggio di un riepilogo giornaliero.
 */
export function buildPrefsPayload(p: SupportEmailPrefs): SupportEmailPrefsInput {
  const out: SupportEmailPrefsInput = { mode: p.mode, urgentImmediate: p.urgentImmediate };
  if (p.mode === 'hours') out.hours = p.hours;
  if (p.mode === 'daily' || p.mode === 'weekly') out.digestTime = p.digestTime;
  if (p.mode === 'weekly') out.digestDay = p.digestDay;
  return out;
}

export interface NotificationPreferencesProps {
  api: NotificationPrefsApi;
  variant?: NotificationPrefsVariant;
}

const CARD_CLASS: Record<NotificationPrefsVariant, string> = {
  admin: 'a2-card p-4 sm:p-5',
  associative: 'rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 sm:p-5',
};

const TEXT = 'text-sm text-gray-700 dark:text-gray-200';
const MUTED = 'text-xs text-gray-500 dark:text-gray-400';
const INPUT =
  'rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-sm text-gray-800 dark:text-gray-100 px-2 py-1';

export const MODE_OPTIONS: { value: SupportEmailMode; label: string; hint: string }[] = [
  { value: 'immediate', label: 'Subito', hint: 'Un\'email per ogni evento, appena succede (come finora).' },
  { value: 'hours', label: 'Solo in fascia oraria', hint: 'Subito dentro la fascia; fuori fascia gli eventi arrivano in un riepilogo alla riapertura.' },
  { value: 'daily', label: 'Riepilogo giornaliero', hint: 'Un\'unica email al giorno, all\'ora scelta, con tutto quello che è successo.' },
  { value: 'weekly', label: 'Riepilogo settimanale', hint: 'Un\'unica email alla settimana, nel giorno e all\'ora scelti.' },
  { value: 'none', label: 'Nessuna email', hint: 'Nessuna email dei ticket, nemmeno per gli urgenti: restano le notifiche nel centro notifiche e la coda.' },
];

export const DAYS: { value: number; short: string; long: string }[] = [
  { value: 1, short: 'Lun', long: 'Lunedì' },
  { value: 2, short: 'Mar', long: 'Martedì' },
  { value: 3, short: 'Mer', long: 'Mercoledì' },
  { value: 4, short: 'Gio', long: 'Giovedì' },
  { value: 5, short: 'Ven', long: 'Venerdì' },
  { value: 6, short: 'Sab', long: 'Sabato' },
  { value: 7, short: 'Dom', long: 'Domenica' },
];

const DEFAULTS: SupportEmailPrefs = {
  mode: 'immediate',
  hours: { from: '09:00', to: '18:00', days: [1, 2, 3, 4, 5] },
  digestTime: '08:30',
  digestDay: 1,
  urgentImmediate: true,
};

/** Errore di validazione lato client (stesse regole del backend), null se ok. */
export function prefsError(p: SupportEmailPrefs): string | null {
  const hhmm = /^([01]\d|2[0-3]):([0-5]\d)$/;
  if (p.mode === 'hours') {
    if (!hhmm.test(p.hours.from) || !hhmm.test(p.hours.to)) return 'Indica gli orari della fascia.';
    if (p.hours.from >= p.hours.to) return 'L\'orario di inizio deve precedere quello di fine.';
    if (!p.hours.days.length) return 'Scegli almeno un giorno.';
  }
  if ((p.mode === 'daily' || p.mode === 'weekly') && !hhmm.test(p.digestTime)) return 'Indica l\'ora del riepilogo.';
  return null;
}

const errMsg = (e: unknown, fallback: string): string => {
  const data = (e as { response?: { data?: { error?: string } } })?.response?.data;
  return (data && data.error) || fallback;
};

const NotificationPreferences: React.FC<NotificationPreferencesProps> = ({ api, variant = 'admin' }) => {
  const toast = useToast();
  const [prefs, setPrefs] = useState<SupportEmailPrefs | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [confirmRevoke, setConfirmRevoke] = useState(false);
  const [revoking, setRevoking] = useState(false);

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      const p = await api.getNotificationPreferences();
      setPrefs({ ...DEFAULTS, ...p, hours: { ...DEFAULTS.hours, ...(p && p.hours) } });
    } catch (e) {
      setLoadError(errMsg(e, 'Impossibile caricare le preferenze.'));
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const error = useMemo(() => (prefs ? prefsError(prefs) : null), [prefs]);

  const update = (patch: Partial<SupportEmailPrefs>) => setPrefs((p) => (p ? { ...p, ...patch } : p));
  const updateHours = (patch: Partial<SupportEmailPrefs['hours']>) =>
    setPrefs((p) => (p ? { ...p, hours: { ...p.hours, ...patch } } : p));
  const toggleDay = (d: number) =>
    setPrefs((p) => {
      if (!p) return p;
      const has = p.hours.days.includes(d);
      const days = has ? p.hours.days.filter((x) => x !== d) : [...p.hours.days, d].sort((a, b) => a - b);
      return { ...p, hours: { ...p.hours, days } };
    });

  const save = async () => {
    if (!prefs || error) return;
    setSaving(true);
    try {
      const saved = await api.saveNotificationPreferences(buildPrefsPayload(prefs));
      if (saved) setPrefs({ ...DEFAULTS, ...saved, hours: { ...DEFAULTS.hours, ...saved.hours } });
      toast.success('Preferenze salvate');
    } catch (e) {
      toast.error('Salvataggio non riuscito', errMsg(e, 'Riprova tra poco.'));
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const r = await api.sendTestDigest();
      toast.success('Email di prova inviata', r && r.to ? `Controlla la casella ${r.to}` : undefined);
    } catch (e) {
      toast.error('Email di prova non inviata', errMsg(e, 'Riprova tra poco.'));
    } finally {
      setTesting(false);
    }
  };

  // Task 27: i link diretti nelle email aprono una sessione per 72 ore; se una
  // email finisce in mani sbagliate si invalidano tutti quelli gia inviati.
  const revokeLinks = async () => {
    if (!api.revokeSupportLinks) return;
    setConfirmRevoke(false);
    setRevoking(true);
    try {
      await api.revokeSupportLinks();
      toast.success('Link invalidati', 'I link nelle email gia ricevute non funzionano piu.');
    } catch (e) {
      toast.error('Operazione non riuscita', errMsg(e, 'Riprova tra poco.'));
    } finally {
      setRevoking(false);
    }
  };

  const card = CARD_CLASS[variant];

  return (
    <div className="max-w-3xl mx-auto space-y-4">
      <div className={card}>
        <h1 className="text-base font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
          <Bell size={16} aria-hidden="true" /> Notifiche email dei ticket
        </h1>
        <p className={`${TEXT} mt-1`}>
          Scegli come ricevere le email del supporto: nuovi ticket, risposte dei richiedenti, assegnazioni, trasferimenti e SLA.
        </p>
        <p className={`${MUTED} mt-2`}>
          Le notifiche nel centro notifiche e nella coda restano immediate; le email agli associati non cambiano.
        </p>
      </div>

      {loadError && (
        <div className={card} role="alert">
          <p className="text-sm text-red-600 dark:text-red-400">{loadError}</p>
          <button type="button" onClick={load} className="mt-2 text-sm underline text-gray-700 dark:text-gray-200">Riprova</button>
        </div>
      )}

      {!prefs && !loadError && (
        <div className={`${card} flex items-center gap-2 ${TEXT}`}>
          <Loader2 size={14} className="animate-spin" aria-hidden="true" /> Caricamento…
        </div>
      )}

      {prefs && (
        <div className={card}>
          <fieldset>
            <legend className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">Quando ricevere le email</legend>
            <div className="space-y-2">
              {MODE_OPTIONS.map((o) => (
                <label key={o.value} className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="support-email-mode"
                    value={o.value}
                    checked={prefs.mode === o.value}
                    onChange={() => update({ mode: o.value })}
                    className="mt-0.5"
                  />
                  <span>
                    <span className="text-sm font-medium text-gray-800 dark:text-gray-100">{o.label}</span>
                    <span className={`block ${MUTED}`}>{o.hint}</span>
                  </span>
                </label>
              ))}
            </div>
          </fieldset>

          {prefs.mode === 'hours' && (
            <div className="mt-4 space-y-3 border-t border-gray-200 dark:border-gray-700 pt-3" data-testid="fields-hours">
              <div className="flex flex-wrap items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
                <span>Dalle</span>
                <input type="time" aria-label="Inizio fascia" className={INPUT} value={prefs.hours.from}
                  onChange={(e) => updateHours({ from: e.target.value })} />
                <span>alle</span>
                <input type="time" aria-label="Fine fascia" className={INPUT} value={prefs.hours.to}
                  onChange={(e) => updateHours({ to: e.target.value })} />
              </div>
              <div className="flex flex-wrap gap-1.5" role="group" aria-label="Giorni della fascia">
                {DAYS.map((d) => {
                  const on = prefs.hours.days.includes(d.value);
                  return (
                    <button
                      key={d.value}
                      type="button"
                      aria-pressed={on}
                      title={d.long}
                      onClick={() => toggleDay(d.value)}
                      className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                        on
                          ? 'bg-blue-600 border-blue-600 text-white'
                          : 'bg-transparent border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300'
                      }`}
                    >
                      {d.short}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {(prefs.mode === 'daily' || prefs.mode === 'weekly') && (
            <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-gray-200 dark:border-gray-700 pt-3 text-sm text-gray-700 dark:text-gray-200" data-testid="fields-digest">
              {prefs.mode === 'weekly' && (
                <>
                  <span>Ogni</span>
                  <select aria-label="Giorno del riepilogo" className={INPUT} value={prefs.digestDay}
                    onChange={(e) => update({ digestDay: Number(e.target.value) })}>
                    {DAYS.map((d) => <option key={d.value} value={d.value}>{d.long}</option>)}
                  </select>
                </>
              )}
              <span>{prefs.mode === 'weekly' ? 'alle' : 'Ogni giorno alle'}</span>
              <input type="time" aria-label="Ora del riepilogo" className={INPUT} value={prefs.digestTime}
                onChange={(e) => update({ digestTime: e.target.value })} />
              <span className={`basis-full ${MUTED}`}>
                Nel riepilogo trovi anche le note interne dei colleghi che non hai ancora letto.
              </span>
            </div>
          )}

          {(prefs.mode === 'hours' || prefs.mode === 'daily' || prefs.mode === 'weekly') && (
            <label className="mt-4 flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={prefs.urgentImmediate}
                onChange={(e) => update({ urgentImmediate: e.target.checked })}
                className="mt-0.5"
              />
              <span className="text-sm text-gray-700 dark:text-gray-200">
                Gli eventi urgenti arrivano subito comunque (priorità urgente, ticket assegnato o trasferito a me, SLA)
              </span>
            </label>
          )}

          {error && <p className="mt-3 text-sm text-red-600 dark:text-red-400" role="alert">{error}</p>}

          <div className="mt-4 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={save}
              disabled={saving || !!error}
              className="inline-flex items-center gap-1.5 rounded-md bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm px-3 py-1.5"
            >
              {saving && <Loader2 size={13} className="animate-spin" aria-hidden="true" />} Salva
            </button>
            <button
              type="button"
              onClick={sendTest}
              disabled={testing}
              className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 text-sm px-3 py-1.5"
            >
              {testing ? <Loader2 size={13} className="animate-spin" aria-hidden="true" /> : <Send size={13} aria-hidden="true" />} Invia email di prova
            </button>
          </div>
          <p className={`${MUTED} mt-2`}>L&apos;email di prova usa il modello del riepilogo con dati di esempio (massimo 3 all&apos;ora).</p>
        </div>
      )}

      {prefs && api.revokeSupportLinks && (
        <div className={card}>
          <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Link nelle email</h2>
          <p className={`${TEXT} mt-1`}>
            Il link «Apri» nelle email dei ticket ti fa entrare direttamente, senza login, per 72 ore.
            Se hai inoltrato un&apos;email per errore o temi che qualcuno l&apos;abbia letta, invalida i link già inviati.
          </p>
          <p className={`${MUTED} mt-1`}>
            {variant === 'admin'
              ? 'Chiude anche le sessioni aperte (compresa questa, al prossimo rinnovo): poi servirà un nuovo accesso.'
              : 'Le sessioni già aperte restano valide fino alla loro scadenza (7 giorni).'}
          </p>
          <button
            type="button"
            onClick={() => setConfirmRevoke(true)}
            disabled={revoking}
            className="mt-3 inline-flex items-center gap-1.5 rounded-md border border-red-300 dark:border-red-700 text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50 text-sm px-3 py-1.5"
          >
            {revoking ? <Loader2 size={13} className="animate-spin" aria-hidden="true" /> : <Link2Off size={13} aria-hidden="true" />} Invalida i link già inviati
          </button>
        </div>
      )}

      <ConfirmDialog
        isOpen={confirmRevoke}
        onClose={() => setConfirmRevoke(false)}
        onConfirm={revokeLinks}
        title="Invalidare i link già inviati?"
        message="I link nelle email dei ticket che hai già ricevuto smetteranno di funzionare. Le prossime email avranno link nuovi."
        confirmText="Invalida"
        type="warning"
      />
    </div>
  );
};

export default NotificationPreferences;
